export interface CategoryConfig {
  id: string;
  name: string;
  dbName: string;
  path: string;
  legacyPaths: string[];
  title: string;
  description: string;
  keywords: string;
  h1: string;
  subtitle: string;
  heroImage: string;
  infoImage: string;
  infoImageAlt: string;
  intro: string[];
  guideTitle: string;
  guide: string[];
  bullets: string[];
  relatedBlog?: { text: string; slug: string };
  loadingLabel: string;
  emptyLabel: string;
  accent: 'emerald' | 'blue' | 'gray';
}

export const categories: CategoryConfig[] = [
  {
    id: 'sadler',
    name: 'Sadler',
    dbName: 'Sadler',
    path: '/sadler',
    legacyPaths: ['/saddles'],
    title: 'Sadler – Dressur, Spring & Western | Sammenlign Priser',
    description: 'Sammenlign priser på dressursadler, springsadler og westernsadler fra danske forhandlere. Se pasform, disciplin og aktuelle tilbud på sadler til hest.',
    keywords: 'sadler, dressursadel, springsadel, western sadel, sadel til hest, sadeltilpasning, sadler danmark',
    h1: 'Sadler til dressur, spring og western',
    subtitle: 'Sammenlign priser på sadler fra danske forhandlere – efter disciplin, pasform og budget',
    heroImage: '/images/categories/cat-sadler.jpg',
    infoImage: '/images/categories/cat-sadler.jpg',
    infoImageAlt: 'Sadeltilpasning i stalden',
    intro: [
      'En sadel skal passe både hest og rytter. For hesten betyder det frihed i skulder og ryg, jævnt tryk og plads til manke. For rytteren betyder det et sæde, der støtter den siddestilling disciplinen kræver – dybt i dressur, mere fremad i spring.',
      'På EquiNord kan du sammenligne sadler fra flere danske forhandlere ét sted. Vi viser pris, mærke og lagerstatus, så du kan se forskellen på en alsidig sadel til hverdagsridning og en specialsadel til stævner, før du går videre til butikken.',
      'Start med hestens rygmål og din disciplin, ikke med farve eller mærke. En billig sadel, der nager, bliver dyr i dyrlæge og mistet ridetid. Brug listen herunder til at filtrere, og læs vores guide til sadelvalg, hvis du er i tvivl om dressur, spring eller western.'
    ],
    guideTitle: 'Sådan vælger du sadel',
    guide: [
      'Dressursadler har langt sæde og lange flapper, så benet kan ligge stille. Springsadler har fremadskårne flapper og et fladere sæde til let sæde. Alsidige sadler er et fornuftigt kompromis til skovture og blandet træning. Westernsadler fordeler vægten over et større område og egner sig til lange ture.',
      'Få hestens ryg målt – eller brug en sadelmager – før du køber. Tjek at bomvidden matcher, at sadlen ikke ligger på manken, og at den ikke vugger. Mange forhandlere tilbyder prøveperiode; brug den. En sadel, der passer i stalden, skal også testes i trav og galop.',
      'Læder kræver sæbe og fedt, men holder i årevis, hvis det passes. Syntetiske sadler vejer mindre og tåler vejr bedre. Sammenlign priser her, og gå derefter til forhandleren for pasform, bytte og garanti.'
    ],
    bullets: [
      'Sammenlign dressur-, spring- og westernsadler',
      'Se aktuelle priser fra flere danske shops',
      'Læs guide til sadeltilpasning før køb'
    ],
    relatedBlog: { text: 'Læs: Sådan vælger du den rigtige sadel', slug: '/blog/saadan-vaelger-du-den-rigtige-sadel' },
    loadingLabel: 'Indlæser sadler...',
    emptyLabel: 'Ingen sadler tilgængelige lige nu.',
    accent: 'emerald'
  },
  {
    id: 'ridebeklaedning',
    name: 'Ridebeklædning',
    dbName: 'Ridebeklædning',
    path: '/ridebeklaedning',
    legacyPaths: ['/riding-clothes'],
    title: 'Ridebeklædning – Bukser, Støvler & Hjelme | Sammenlign Priser',
    description: 'Find ridebukser, ridestøvler, jakker og sikkerhedshjelme fra danske forhandlere. Sammenlign priser på ridebeklædning til træning og stævne.',
    keywords: 'ridebeklædning, ridebukser, ridestøvler, ridehjelm, ridejakke, ridehandsker, sikkerhedsvest',
    h1: 'Ridebeklædning til træning og stævne',
    subtitle: 'Ridebukser, støvler, jakker og hjelme – sammenlign priser fra danske rideudstyrsbutikker',
    heroImage: '/images/categories/cat-ridebeklaedning.jpg',
    infoImage: '/images/categories/cat-ridebeklaedning.jpg',
    infoImageAlt: 'Ridebeklædning og hjelm',
    intro: [
      'Ridebeklædning skal kunne det samme i januar i en trækfuld ridehal som til et stævne i juli: sidde fast i sadlen, holde varmen eller ånde, og beskytte hoved og ryg. En hjelm med gammel certificering eller ridebukser uden greb er et dårligt sted at spare.',
      'Her kan du sammenligne ridebukser (fuldsæde og knægrip), ridestøvler, chaps, jakker, handsker og hjelme fra flere danske shops. Priserne er de samme som hos forhandleren; EquiNord er et overblik, ikke et ekstra lag oven på prisen.',
      'Prioritér hjelm og sikkerhedsvest først, derefter bukser og støvler, der matcher din disciplin. Stil og farve kommer til sidst. Læs vores beklædningsguide, hvis du er i tvivl om materialer, pasform og hvad der er værd at betale extra for.'
    ],
    guideTitle: 'Hvad skal du kigge efter?',
    guide: [
      'Ridehjelme skal være intakte og følge gældende sikkerhedsnorm. Skift hjelmen efter et fald eller når skallen er ældre end producentens anbefaling – typisk fem år. En vest med rygbeskyttelse er relevant til spring, ungryttere og ved ude-ridning.',
      'Ridebukser med fuldsæde giver mere greb i dressur; knægrip er oftere nok til spring og hverdag. Tekniske stoffer tørrer hurtigt og bærer sved væk. Ridestøvler skal støtte anklen uden at trykke; korte støvler plus chaps er praktiske i stalden, lange støvler ser rigtige ud til stævne.',
      'Jakker og softshell skal kunne rumme en vest under og stadig give armene frihed. Handsker med silikoneprint holder tøjlerne i regn. Sammenlign priser her, og køb hos forhandleren, så bytte af størrelse er enkelt.'
    ],
    bullets: [
      'Hjelme og veste før mode',
      'Fuldsæde vs. knægrip efter disciplin',
      'Sammenlign priser på bukser, støvler og jakker'
    ],
    relatedBlog: { text: 'Læs: Ridebeklædning – investér i kvalitet der holder', slug: '/blog/ridebeklædning-guide-kvalitet' },
    loadingLabel: 'Indlæser ridebeklædning...',
    emptyLabel: 'Ingen ridebeklædning tilgængelig lige nu.',
    accent: 'blue'
  },
  {
    id: 'staldinventar',
    name: 'Staldinventar',
    dbName: 'Staldinventar',
    path: '/staldinventar',
    legacyPaths: ['/stable-equipment'],
    title: 'Staldinventar – Spande, Dækkener & Grime | Sammenlign Priser',
    description: 'Sammenlign priser på staldinventar: foderspande, dækkener, grimer, boksmåtter og hverdagsudstyr til stalden fra danske forhandlere.',
    keywords: 'staldinventar, staldudstyr, foderspande, hestegrime, dækkener, boksmåtter, stalddrift',
    h1: 'Staldinventar til hverdagen i stalden',
    subtitle: 'Spande, dækkener, grimer og praktisk udstyr – sammenlign priser til stalden',
    heroImage: '/images/categories/cat-staldinventar.jpg',
    infoImage: '/images/categories/cat-staldinventar.jpg',
    infoImageAlt: 'Organiseret staldinventar',
    intro: [
      'Staldinventar er det, der gør hverdagen sikker og hurtig: rene spande, grimer der ikke nager, dækkener til dansk vejr og kroge, så seletøjet ikke ligger i sandet. Dårligt udstyr slider på både hest og tid.',
      'Her finder du foderspande, vandspande, grimer, dækkener, boksmåtter og andet staldudstyr fra danske forhandlere. Vi samler priserne, så du kan se, om et dækken eller en grime er fair prissat, før du klikker videre til shoppen.',
      'Tænk i zoner: foder, seletøj, pleje og værktøj. Når hver ting har en plads, bliver aftenrutinen kortere, og du opdager hurtigere, hvis noget mangler. Læs også vores guide til staldorganisering, hvis du vil have en fast rutine på plads.'
    ],
    guideTitle: 'Udstyr der holder i en dansk stald',
    guide: [
      'Spande skal kunne tåle frost, spark og daglig skrubning. Vælg foder- og vandspande, du kan skille ad eller vaske i koldt vand om vinteren. Automatiske vandere sparer tid, men skal tjekkes for is og alger.',
      'Grimer skal passe ved nakkestykke og næsebånd uden at gnave. Fleeceforede modeller er skånsomme i frost; rebgrimer er lette til fold. Dækkener vælges efter fill (gram), denier og om hesten går ude eller inde – et 0g regndækken og et mid-weight tæppe dækker de fleste danske vintre bedre end ét tungt tæppe.',
      'Boksmåtter, kroge og boksgitre er kedelige køb, indtil de mangler. Køb holdbart, og sammenlign priser her. Selve købet sker hos forhandleren med deres fragt og retur.'
    ],
    bullets: [
      'Spande, grimer og dækkener til dansk klima',
      'Sammenlign priser på hverdagsudstyr',
      'Se guide til at organisere stalden'
    ],
    relatedBlog: { text: 'Læs: Sådan organiserer du en effektiv stald', slug: '/blog/staldledelse-organisering' },
    loadingLabel: 'Indlæser staldinventar...',
    emptyLabel: 'Intet staldinventar tilgængeligt lige nu.',
    accent: 'gray'
  },
  {
    id: 'pleje',
    name: 'Pleje',
    dbName: 'Pleje',
    path: '/pleje',
    legacyPaths: ['/grooming'],
    title: 'Hestepleje – Børster, Hovpleje & Shampoo | Sammenlign Priser',
    description: 'Sammenlign priser på hestepleje: børster, hovkroge, hovolie, shampoo og plejeværktøj fra danske forhandlere. Hold hest og hove i orden.',
    keywords: 'hestepleje, plejeværktøj, hestebørster, hovkrog, hovolie, hesteshampoo, grooming',
    h1: 'Plejeudstyr til hest og hove',
    subtitle: 'Børster, hovkroge, olie og shampoo – sammenlign priser på hestepleje',
    heroImage: '/images/categories/cat-pleje.jpg',
    infoImage: '/images/categories/cat-pleje.jpg',
    infoImageAlt: 'Hestepleje med børste',
    intro: [
      'Daglig pleje er den billigste sundhedsforsikring, du har: du mærker varme i hove, skurv, skader og flåter, før de bliver dyre. Et minimalt sæt – stryglebørste, stiv børste, blød børste, hovkrog og en klud – dækker de fleste heste.',
      'Her kan du sammenligne børster, hovkroge, hovolie, shampoo og øvrigt plejeværktøj fra danske forhandlere. Priserne er butikkens egne; vi viser dem samlet, så du ikke betaler overpris for et basalt kit.',
      'Hovkrogen er det stykke, du skal bruge hver dag. Resten af sættet kan du bygge op efter pels, årstid og om hesten klippes. Læs vores lister over essentielle værktøjer og hovpleje, hvis du vil have rækkefølgen i stallgangen på plads.'
    ],
    guideTitle: 'Pleje der passer til dansk vejr',
    guide: [
      'Start med stryglebørste i cirkler, derefter stiv børste med hårene, og afslut med blød børste. Metalstrigle hører ikke til på ben og hoved. Hold separate svampe til ansigt og bagpart.',
      'Hove: rens fra hæl mod tå, tjek stråle og lugt, og se efter varme. Hovolie eller fedt kan hjælpe i tørre perioder, men løser ikke dårlig beskæring eller mudder. Shampoo bruges sparsomt – for meget vask udtørrer hud og pels, især om vinteren.',
      'Opbevar børster tørt, og skift dem, når hårene er slået. Et kit i en spand eller taske gør det nemmere at have det med på stævne. Køb hos forhandleren via links her, så du har deres bytte- og kvalitetsret.'
    ],
    bullets: [
      'Hovkrog og børstesæt til daglig brug',
      'Hovolie, shampoo og pleje efter årstid',
      'Guides til hovpleje og basisværktøj'
    ],
    relatedBlog: { text: 'Læs: 10 essentielle plejeværktøjer', slug: '/blog/10-essentielle-plejevarktojer' },
    loadingLabel: 'Indlæser plejeudstyr...',
    emptyLabel: 'Intet plejeudstyr tilgængeligt lige nu.',
    accent: 'emerald'
  },
  {
    id: 'foder',
    name: 'Hestefoder & Tilskud',
    dbName: 'Foder',
    path: '/foder',
    legacyPaths: ['/feed'],
    title: 'Hestefoder & Tilskud – Hø, Vitaminer & Mineraler',
    description: 'Sammenlign priser på hestefoder, tilskud, vitaminer og mineraler fra danske forhandlere. Find grovfoder, pellets og vinterfoder til hest.',
    keywords: 'hestefoder, foder til heste, tilskud, vitaminer til heste, mineraler, pellets, grovfoder',
    h1: 'Hestefoder og tilskud',
    subtitle: 'Sammenlign priser på foder, vitaminer og mineraler fra danske forhandlere',
    heroImage: '/images/categories/cat-foder.jpg',
    infoImage: '/images/categories/cat-foder.jpg',
    infoImageAlt: 'Opmåling af hestefoder',
    intro: [
      'Hestens foderplan starter med grovfoder – hø eller wrap i mængde og kvalitet, der matcher vægt, arbejde og tænder. Kraftfoder og tilskud er til det, grovfoderet ikke dækker: energi til arbejde, mineraler, eller ekstra kalorier til ældre og magre heste.',
      'På denne side kan du sammenligne hestefoder, pellets, müsli, vitaminer og mineraler fra danske shops. Brug det til at se pris pr. sæk og hvad der faktisk er på lager, før du lægger en fast foderplan om.',
      'Ændr aldrig foder brat. Nye sække og nye tilskud indfases over dage, og vand skal være tit og tø-frit om vinteren. Vores vinterfoderguide dækker hø som varmekilde, vand og typiske fejl i kulde.'
    ],
    guideTitle: 'Foder uden gætteri',
    guide: [
      'De fleste heste i let arbejde klarer sig på godt grovfoder plus en balanceret mineralblanding. Kraftfoder giver mening ved højt arbejde, dårligt grovfoder eller heste, der taber sig. Tilskud som biotin, elektrolytter eller maveprodukter skal have et klart formål – ikke en hylde fuld af “måske”.',
      'Læs tørstof, sukker og stivelse, hvis hesten er øm i hovene eller let fed. Ældre heste kan have brug for opblødt foder. Hold vægt og huld, ikke kun sækkenes marketing.',
      'Priser her er forhandlerens. Køb der, hvor du kan få den samme sæk igen, og hvor holdbarhed og opbevaring er i orden. EquiNord er sammenligning, ikke et foderfirma.'
    ],
    bullets: [
      'Grovfoder først, tilskud med formål',
      'Sammenlign priser på foder og mineraler',
      'Guide til vinterfodring'
    ],
    relatedBlog: { text: 'Læs: Vinterfoder til heste', slug: '/blog/vinterfoder-til-heste-guide' },
    loadingLabel: 'Indlæser foder...',
    emptyLabel: 'Intet foder tilgængeligt lige nu.',
    accent: 'blue'
  },
  {
    id: 'hestetilbehor',
    name: 'Hestetilbehør',
    dbName: 'Hestetilbehør',
    path: '/hestetilbehor',
    legacyPaths: [],
    title: 'Hestetilbehør – Trense, Dækken, Grime & Udstyr',
    description: 'Sammenlign priser på hestetilbehør: trenser, dækkener, grimer, underlag, gamacher og klokker fra danske forhandlere.',
    keywords: 'hestetilbehør, trense, hestedækken, grime, underlag, gamacher, klokker, seletøj',
    h1: 'Hestetilbehør til ridning og fold',
    subtitle: 'Trenser, dækkener, grimer, underlag og øvrigt udstyr – sammenlign priser',
    heroImage: '/images/categories/cat-hestetilbehor.jpg',
    infoImage: '/images/categories/cat-hestetilbehor.jpg',
    infoImageAlt: 'Seletøj og hestetilbehør',
    intro: [
      'Hestetilbehør dækker det, der ikke er sadel, tøj eller foder: trenser, bid, grimer, dækkener, underlag, gamacher, klokker og lignende. Det er den største produktgruppe i kataloget, fordi mange feeds lander her, når varen ikke klart er en sadel eller et par ridebukser.',
      'Brug siden til at sammenligne priser på trenser, dækkener og hverdagsudstyr fra danske forhandlere. Kig på pasform og materialer – et dækken i forkert rygmål eller en trense, der nager, er spild, uanset prisen.',
      'Hvis du leder efter noget mere specifikt, så start med sadler, pleje eller staldinventar. Her samler vi resten, så du stadig kan finde varen og se, hvilken shop der sælger den.'
    ],
    guideTitle: 'Find det rigtige tilbehør',
    guide: [
      'Trenser skal passe ved nakke, kæbe og bidvinkel. Et bid er ikke “ét til alle heste”; tykkelse, materiale og skank afhænger af mund og uddannelse. Få hjælp i butikken, hvis hesten ryster på hovedet eller åbner munden.',
      'Dækkener: mål ryggen, vælg denier efter hvor hårdt tæppet skal tåle fold, og fill efter temperatur. Underlag skal holde sadlen stabil uden at løfte den af ryggen. Gamacher og klokker er til at beskytte ben – de skal være rene og tørre, ellers gør de mere skade end gavn.',
      'Sammenlign priser her, og køb hos forhandleren. EquiNord tager ikke selv imod betaling eller retur; det gør shoppen, du lander i.'
    ],
    bullets: [
      'Trenser, dækkener, grimer og benbeskyttelse',
      'Sammenlign priser på et stort udvalg',
      'Gå til specialkategorier når du kender typen'
    ],
    relatedBlog: { text: 'Læs: Forårsklargøring af hest og udstyr', slug: '/blog/foraarsklargoring-hest' },
    loadingLabel: 'Indlæser hestetilbehør...',
    emptyLabel: 'Intet hestetilbehør tilgængeligt lige nu.',
    accent: 'emerald'
  }
];

export function getCategoryByPath(pathname: string): CategoryConfig | undefined {
  const path = pathname.replace(/\/$/, '') || '/';
  return categories.find(c => c.path === path || c.legacyPaths.includes(path));
}

export function getCategoryByDbName(name: string): CategoryConfig | undefined {
  const needle = (name || '').toLowerCase();
  return categories.find(c => c.dbName.toLowerCase() === needle || c.name.toLowerCase() === needle);
}

export function getCategoryPathByName(name: string): string {
  return getCategoryByDbName(name)?.path || '/';
}
